import { ReactNode } from 'react';
import { Client } from 'helpers/hooks/useEmployeeDetails';
import { TFunction } from 'i18next';
import { BaseSchema } from 'yup';
import { Tooltip, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { IconCreditCardRefund, IconEdit } from '@tabler/icons-react';
import { Button } from '@atoms';
import { CLIENT, CORPORATE_CLIENT, OPERATIONS, OPERATIONS_SUPERVISOR } from '@globalConstants';
import i18n from '@i18n';
import { NubiTheme } from '@theme';
import { formatDateV2, parseAmount } from '@utils';
import {
  employeeDetailsClick,
  EVENT_EMPLOYEE_DETAILS_RECOVER_BALANCE,
} from '@modules/employeeDetails/events';
import { Product } from '../useProducts';

const t = i18n.t;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface RowItem<T, O = unknown> {
  label: string;
  value: (data: T, client: Client) => ReactNode;
  inputName?: Extract<keyof Product, string>;
  inputType?: 'select';
  getInitialValue?: (data: T, t?: TFunction) => unknown;
  ActionComponent?: ReactNode;
  customStyle?: {
    label?: SystemStyleObject<NubiTheme> | ((theme: NubiTheme) => SystemStyleObject<NubiTheme>);
    value?: SystemStyleObject<NubiTheme> | ((theme: NubiTheme) => SystemStyleObject<NubiTheme>);
  };
  shouldHideEdit?: (item: T) => boolean;
  inputValidation?: BaseSchema;
  props?: {
    multiple: boolean;
    showCheckbox: boolean;
  };
  editable?: boolean;
  internalFeature?: boolean;
}
type RowsPropType = { canViewOption: boolean; disabled: boolean; onClick: () => void };
interface RowsProps {
  categories: RowsPropType;
  balanceRecovery: RowsPropType;
}

export const CATEGORIES_EDITOR_ROLES = [
  OPERATIONS,
  OPERATIONS_SUPERVISOR,
  CLIENT,
  CORPORATE_CLIENT,
];

/** Returns the rows to be displayed in the product card */
export const ROWS = ({ categories, balanceRecovery }: RowsProps): RowItem<Product, string>[] => [
  {
    label: 'COMMON:CUSTOMER_NAME',
    value: (product) => product.customer?.legal_name,
    internalFeature: true,
  },
  {
    label: 'COMMON:AREA',
    value: (product) => product.area_name,
  },
  {
    label: 'COMMON:SUBSEGMENT',
    value: (product) => product?.sub_segment ?? t('EMPLOYEES:DETAIL:NO_ASSIGNATION'),
    internalFeature: true,
  },
  { label: 'COMMON:PRODUCT', value: (product) => product.product },
  { label: 'COMMON:CVU', value: (product) => product.cvu },
  { label: 'COMMON:ALIAS', value: (product) => product.alias },
  {
    label: 'EMPLOYEES:DETAIL:BENEFITS_AVAILABLE',
    value: (product) => {
      if (!Array.isArray(product.categories)) return '-';

      return product.categories
        .map((category) => {
          if (category === 'GENERIC') {
            return 'Ilimitado';
          }
          return category.charAt(0) + category.substring(1).toLocaleLowerCase();
        })
        .join(', ');
    },
    inputName: 'categories',
    getInitialValue: (product) => product.categories,
    inputType: 'select',
    props: {
      multiple: true,
      showCheckbox: true,
    },
    shouldHideEdit: (product) =>
      product.categories?.[0] === 'No se pudieron cargar los beneficios. Intente nuevamente.',
    editable: true,
    ActionComponent: categories.canViewOption && (
      <Button
        text='EMPLOYEES:DETAIL:EDIT_CATEGORIES'
        startIcon={<IconEdit />}
        size='small'
        variant='outlined'
        disabled={categories.disabled}
        onClick={categories.onClick}
      />
    ),
  },
  {
    label: 'EMPLOYEES:DETAIL:BALANCE_AVAILABLE',
    value: (product) =>
      product.balance_available === undefined ? (
        <Tooltip title={t('COMMON:HIDE_AMOUNT_TOOLTIP')} arrow>
          <Typography variant='body1' component='span'>
            {t('COMMON:HIDE_AMOUNT')}
          </Typography>
        </Tooltip>
      ) : (
        parseAmount(product.balance_available)
      ),
    ActionComponent: balanceRecovery.canViewOption && (
      <Button
        text='EMPLOYEES:DETAIL:RETRIEVE_BALANCE'
        startIcon={<IconCreditCardRefund />}
        size='small'
        variant='outlined'
        disabled={balanceRecovery.disabled}
        onClick={() => {
          employeeDetailsClick(EVENT_EMPLOYEE_DETAILS_RECOVER_BALANCE);
          balanceRecovery.onClick();
        }}
      />
    ),
  },
  {
    label: 'COMMON:FROZEN_BALANCE',
    value: (product) =>
      product?.balance_frozen > 0 ? parseAmount(product.balance_frozen) : undefined,
  },
  {
    label: 'EMPLOYEES:DETAIL:ACCOUNT_CREATED_AT',
    value: (product) => (product.created_at ? formatDateV2(product.created_at) : undefined),
  },
  {
    label: 'COMMON:UPDATED_AT',
    value: (product) => (product.modified_at ? formatDateV2(product.modified_at) : undefined),
  },
  {
    label: 'EMPLOYEES:DETAIL:ENABLE_DISPERSION',
    value: () => {
      return null;
    },
  },
];

export const EDIT_ERRORS = {
  SAME_PRODUCT_CATEGORIES: '4001',
};
